import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { standardGetHttpOptions, standardHttpOptions } from '../../shared/models';
import { LoadingService } from '../../shared/services/loading.service';
import {Observable, throwError} from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { finalize, catchError, map } from 'rxjs/operators';
import { ChartFavoriteRequest } from '../../shared/models/ChartFavorite/chartFavoriteRequest.model';
import { ChartFavoriteInfo } from '../../shared/models/ChartFavorite/chartfavoriteInfo.model';
import { ChartLabelInfo } from '../../shared/models/ChartLabel/chartLabelInfo.model';
import { ChartLabelRequest } from '../../shared/models/ChartLabel/chartLabelRequest.model';
import { of } from 'rxjs';
import { UserService } from '@services/user.service';


@Injectable()
export class SensorReadingsService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  constructor(private http: HttpClient,
        private loadingService: LoadingService,
        @Inject(KIG_SETTINGS) private settings: KigSettings,
        private userService: UserService) {
    this.apiUrl = this.settings.apiUrl;
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }


  public getChartFavorites(): Observable<any> {
    this.loadingService.setMessage('Loading chart favorites ...');
    return this.http.get<ChartFavoriteInfo[]>(`${this.apiUrl}/chartfavorite`,  standardGetHttpOptions).pipe(
      map((response: any) => response ),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public createChartFavorite(chartFavoriteRequest: ChartFavoriteRequest) {
    this.loadingService.setMessage('Creating chart favorite ...');
    return this.http.post<ChartFavoriteRequest>(`${this.apiUrl}/chartfavorite`, chartFavoriteRequest, standardHttpOptions).pipe(
      map((response: any) => {
        if (response != null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public updateChartFavorite(chartFavoriteRequest: ChartFavoriteRequest, username: string) {
    this.loadingService.setMessage('Updating chart favorite ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put<ChartFavoriteRequest>(`${this.apiUrl}/chartfavorite`, chartFavoriteRequest, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public deleteChartFavorite(chartFavoriteId: number, username: string) {
    this.loadingService.setMessage('Deleting chart favorite ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${this.apiUrl}/chartfavorite/${chartFavoriteId}`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  // ----- Chart labels (annotations overlaid on a chart favorite) -----

  public getChartLabels(chartFavoriteId: number): Observable<any> {
    return this.http.get<ChartLabelInfo[]>(`${this.apiUrl}/chartlabel/forfavorite/${chartFavoriteId}`, standardGetHttpOptions).pipe(
      map((response: any) => response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  // Resolves to the new label's numeric id on success.
  public createChartLabel(chartLabelRequest: ChartLabelRequest): Observable<any> {
    return this.http.post<number>(`${this.apiUrl}/chartlabel`, chartLabelRequest, standardHttpOptions).pipe(
      map((response: any) => response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public updateChartLabel(chartLabelRequest: ChartLabelRequest, username: string): Observable<any> {
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${this.apiUrl}/chartlabel`, chartLabelRequest, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public deleteChartLabel(chartLabelId: number, username: string): Observable<any> {
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${this.apiUrl}/chartlabel/${chartLabelId}`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => {
        if (response == null) {
          return 'success';
        }
        return response;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(error);
  }
}
